import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, SelectChangeEvent, Typography } from '@mui/material'
import axios from 'lib/axios'
import { normalizeMetricStatus } from 'lib/metricStatus'
import dayjs from 'dayjs'
import * as d3 from 'd3'

/* Utils */
import { AxiosResponse } from 'axios'
import { useLibraryDetails } from 'contexts/LibraryDetailsContext'
import {
  ChartData,
  ChartDataExtended,
  ChartDataValue,
  CPIFrequency,
  CPIManifestUIVisualizationParamSection,
  CPISeverity,
  CPITimePeriod,
  CustomLabelCountI,
  CustomLabelDate,
  CustomLabelsDataPayload,
  CustomLabelsListI,
  CustomTrendsDataPayload,
  D3Position,
  DataStatus,
  DateRangeI,
  Label,
  LabelCount,
  LabelCountData,
  PredictionChartData,
  SnowflakeChartData,
  TrendsDataPayload,
  VisibleLabel
} from 'models'
import {
  adjustDefaultTimePeriod,
  DEFAULT_TIMER,
  INITIALIZING_TIMER,
  RECALCULATING_TIMER
} from '../../../library/library.constants'
import { checkIsGreen, dateFormat, getHealthDirection } from 'lib/utils'
import {
  addStartEndWeekly,
  extractMonth,
  calculateLabelCount,
  filterData,
  findNearestXIndex,
  getAxisDateRange,
  getPointProximityLimit,
  findUniqueMonths,
  findUniqueMonthsInitializing,
  findUniqueMonthsPredictions,
  findVisibleLabelsXPosition,
  findVisibleLabelsXPositionMonthly,
  findWindowStripes,
  formatPerformanceUnit,
  getIntegrationIds,
  getPathAndPositions,
  getPathAndPositionsOC,
  getPathAndPositionsPredictions,
  getPayloadTimePeriod,
  getPayloadTrendsChart,
  getSingularSectionsName,
  getUtcDateAndTime,
  groupByWeeks,
  instanceOfSnowflake,
  padStripesToWindow,
  payloadToEnumTimePeriod,
  resolveSectionName
} from '../../../library/utils'
import {
  appendPredictionTrendsChartElements,
  appendTrendsChartElements,
  appendTrendsChartOCElements,
  CHART_HEIGHT,
  chartHeightStart,
  getAreaFill,
  getAreaStroke,
  getDataStartsAt,
  getLineColor,
  getYLine,
  MAX_PINNED_LABELS_COUNT,
  onMouseMove,
  positionAddEventButton,
  xAxisHeight
} from './chart-utils'
import { raiseElements } from './chart-components'
import { hideHoverElements } from './chart-components-shared'
import { removeElements } from './chart-components-oc'
import { useAppSelector } from 'store/hooks'
import { selectThemeMode } from 'store/theme/themeSlice'
import { accountSettingsSelector } from 'store/user/userSlice'

/* Components */
import {
  LegendSlaIcon,
  TrendsPerformanceIcon,
  LegendLabelsIcon,
  PredictedPerformanceIcon,
  OnyxAIPredictIcon,
  ExitPredictionModeIcon
} from 'ui/components/svg'
import {
  ControlsBar,
  DataTrendsContainer,
  OnyxAITooltipWrapper,
  OnyxAIWrapper,
  predictBtnSx,
  predictDisabledBtnSx,
  predictionModeExitBtnSx
} from './trendsTab.styles'
import TrendsChart from './TrendsChart'
import TrendsChartDescription from './sections/TrendsChartDescription'
import LabelsDrawer from './labels-drawer/LabelsDrawer'
import TrendsChartSelectField from './components/TrendsChartSelectField'
import DataTrendsControls from './components/DataTrendsControls'
import NoTrendData from './components/NoTrendData'
import { getNoTrendDataCopy } from './components/noTrendData.copy'
import { OnyxAITooltip } from './sections/OnyxAITooltip'
import ThemeButton from 'ui/components/buttons/primary/ThemeButton'

export interface DefaultLastDayProps {
  value: string
  date: string
  predictionValue: string
  predictionDate: string
  lastDayDenominator: number
}

/* Card fallback when no point matches recentDate; tolerates an empty values array (sparse contract). */
const buildLastDayFallbackProps = (
  values: ChartDataValue[],
  dPrediction: { xValue: string; yValue: number } | undefined,
  cpiName: string
): DefaultLastDayProps => {
  const lastDay = values[values.length - 1]

  return {
    value:
      cpiName === 'cpi997'
        ? `${lastDay?.numerator ?? 0} / ${lastDay?.denominator ?? 0}`
        : `${lastDay?.numerator ?? 0}`,
    date: lastDay?.xValue.slice(0, 10) ?? '',
    lastDayDenominator: (lastDay?.numerator || lastDay?.denominator) ?? 0,
    predictionValue: `${dPrediction?.yValue ?? 0}`,
    predictionDate: dPrediction ? dPrediction.xValue.slice(0, 10) : lastDay?.xValue.slice(0, 10) ?? ''
  }
}

const TrendsTab: FC = () => {
  const {
    chartData,
    selectedFrequency,
    selectedTimePeriod,
    selectedSeverity,
    labelDrawerOpen,
    setLabelDrawerOpen,
    predictionFetched,
    isSnowflake,
    cpi,
    setMode,
    stripes,
    setStripes,
    renderingChart,
    selectedType,
    manifestData,
    setCriticalSla,
    setChartData,
    setRenderingChart,
    setShowMessage,
    recalculatingCpiName,
    setRecalculatingCpiName,
    selectedDataSourceIds,
    setSelectedSeverity,
    customTimePeriodValue,
    setCurrentSeverityStatus,
    currentSeverityStatus,
    setRedirectToMode,
    setHealthDirection,
    predictionModalOpen,
    fetchingInterval,
    setFetchingInterval,
    refetchPredictionData,
    hasPredictionsEnabled,
    predictionModelAvailable,
    handlePredictionModalClose,
    handlePredictionModalOpen,
    daysUntilDataAvailable,
    predictionData,
    dataSourceOptions,
    setInitialQueryLoaded,
    trendsRefetchTrigger,
    setDefaultRecentValueDate
  } = useLibraryDetails()
  const location = useLocation()
  const accountSettings = useAppSelector(accountSettingsSelector)
  const themeMode = useAppSelector(selectThemeMode)
  const descriptionWrapperRef = useRef<HTMLDivElement | null>(null)
  const [editLabel, setEditLabel] = useState({} as Label)
  const [customLabels, setCustomLabels] = useState({} as CustomLabelsListI)
  const [customLabelsCount, setCustomLabelsCount] = useState({} as CustomLabelCountI)
  const [customDate, setCustomDate] = useState<CustomLabelDate>({
    date: '',
    start: '',
    end: ''
  })
  const [appliedFilter, setAppliedFilter] = useState({
    startDate: '',
    endDate: '',
    types: []
  })
  const [loading, setLoading] = useState(false)
  const [labelsCount, setLabelsCount] = useState<LabelCountData | null>(null)
  const [currentPositions, setCurrentPositions] = useState<D3Position[]>([])
  const [currentUniqueMonths, setCurrentUniqueMonths] = useState<string[]>([])

  const [chartDateRange, setChartDateRange] = useState({} as DateRangeI)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const svgRef = useRef<SVGSVGElement | null>(null)
  const [maxValueY, setMaxValueY] = useState(100)
  const [slaChip, setSlaChip] = useState<{ x: number; y: number; w: number; h: number; text: string } | null>(null)
  const [width, setWidth] = useState((descriptionWrapperRef as any)?.current?.clientWidth || window.innerWidth - 48)
  const margin = { top: 20, right: 0, bottom: 40, left: 0 }
  const [visibleLabels, setVisibleLabels] = useState<VisibleLabel[]>([])
  const [initialLoad, setInitialLoad] = useState(false)
  const [defaultLastDayProps, setDefaultLastDayProps] = useState<DefaultLastDayProps>({
    value: '',
    date: '',
    predictionValue: '',
    predictionDate: '',
    lastDayDenominator: 0
  })
  const [labelCount, setLabelCount] = useState<number>(0)
  const hasFetchedInitially = useRef(false)
  const prevChartStatusRef = useRef<DataStatus | null>(null)

  const fetchTrendsData = useCallback(
    async (payload: TrendsDataPayload, stripes?: number | null) => {
      if (!cpi || !manifestData) return

      let severities: any = []
      const userInput = manifestData.ui.trend_chart.user_input
      const healthUnit = manifestData.ui.trend_chart.user_input.visualization_param.unit
      if (userInput.formula_params[0]?.sections.length > 0) {
        severities = userInput.formula_params[0]?.sections
      } else if (userInput.visualization_param.sections.length > 1) {
        severities = userInput.visualization_param.sections
      }

      const newSection = severities.find((item: { display_name: CPISeverity }) => item.display_name === payload.section)
      if (newSection) payload.section = newSection.name

      try {
        let res: AxiosResponse<any, any> | null

        if (isSnowflake && cpi && cpi.configuration) {
          const instanceNames =
            payload && payload.integration_ids?.length > 0
              ? payload.integration_ids
              : cpi
              ? cpi.configuration?.integrations.filter((c) => !c.deleted).map((c) => c.instanceName)
              : []

          const snowflakePayload = {
            metric_request: {
              section: payload.section,
              frequency: payload.frequency,
              instance_names: instanceNames,
              ...(payload.timePeriod && {
                time_period: payload.timePeriod
              }),
              ...(payload.dateFrom &&
                payload.dateTo && {
                  date_from: payload.dateFrom,
                  date_to: payload.dateTo
                })
            }
          }
          res = await axios.post(`/api/v4/metrics/${cpi.name}/timeseries?trends-timeseries`, snowflakePayload)
        } else {
          res = await axios.post(`/api/v3/cpis/${cpi.name}/data`, payload)
        }

        if (res && res.status.toString().includes('20')) {
          if (isSnowflake) {
            const { data }: { data: ChartData } = res
            data.status = normalizeMetricStatus(data.status)
            const today = new Date().toISOString()
            const todayDate = today.slice(0, 10)
            const dataLastPoint =
              data.values.length > 0 ? data.values[data.values.length - 1].xValue.slice(0, 10) : undefined
            if (data.status !== DataStatus.Ready && dataLastPoint && dataLastPoint === todayDate) {
              data.values.pop()
            }

            if (recalculatingCpiName === cpi?.name) {
              const isRecalculating = status === DataStatus.Recalculating
              setShowMessage(isRecalculating)
              if (!isRecalculating) setRecalculatingCpiName(null)
            }

            if (data.status === DataStatus.Initializing && !data.values.length) {
              setFetchingInterval(INITIALIZING_TIMER)
              const userInput = manifestData.ui.trend_chart.user_input

              const sectionSla = userInput.visualization_param.sections.find((n) => n.name === selectedSeverity)
              const sla =
                sectionSla && sectionSla.value ? sectionSla.value : userInput.visualization_param.identical_value ?? 0
              const stubValues: ChartDataValue[] = []
              let date = dayjs().format('YYYY-MM-DD')
              Array.from(Array(3).keys()).forEach((n) => {
                let d = date
                if (n !== 0) {
                  const c = dayjs(date).subtract(1, 'month')
                  d = dayjs(c).format('MM-DD-YYYY')
                }
                stubValues.push({
                  xValue: n === 0 ? date : dayjs(d).format('YYYY-MM-DD'),
                  yValue: 0,
                  denominator: 0,
                  numerator: 0
                })
                date = dayjs(d).format('MM-DD-YYYY')
              })
              const today = new Date().toISOString()
              const stubInitializingData: ChartData = {
                recentValue: 0,
                recentDate: today,
                status: DataStatus.Initializing,
                zoneColor: 'green',
                sla,
                updatedAt: today,
                values: stubValues
              }
              setChartData(stubInitializingData)

              return
            }

            const normalizedValues = data.values.map((value) => ({
              ...value,
              yValue: isNaN(value.yValue) ? 0 : value.yValue
            }))
            const normalizedData = {
              ...data,
              values: normalizedValues
            }

            if (!initialLoad) {
              const dPrediction = predictionData?.values[predictionData?.values.length - 1]

              const recentPerformanceDate = getUtcDateAndTime(data.recentDate).labelFormatDate
              const recentPerformanceDataPoint = data.values.find(
                (n) => n.xValue.slice(0, 10) === recentPerformanceDate
              )

              const defaultLastDayValue =
                manifestData.name === 'cpi997'
                  ? `${recentPerformanceDataPoint?.numerator ?? 0} / ${recentPerformanceDataPoint?.denominator ?? 0}`
                  : `${recentPerformanceDataPoint?.numerator ?? 0}`

              if (recentPerformanceDataPoint) {
                const lastDayDenominator =
                  healthUnit === 'percent'
                    ? recentPerformanceDataPoint.numerator
                    : recentPerformanceDataPoint.denominator ?? 0

                setDefaultLastDayProps({
                  value: defaultLastDayValue,
                  date: recentPerformanceDataPoint.xValue.slice(0, 10),
                  lastDayDenominator,
                  predictionValue: `${dPrediction?.yValue ?? 0}`,
                  predictionDate: dPrediction
                    ? dPrediction.xValue.slice(0, 10)
                    : recentPerformanceDataPoint.xValue.slice(0, 10)
                })
              } else {
                setDefaultLastDayProps(buildLastDayFallbackProps(normalizedData.values, dPrediction, manifestData.name))
              }
            }

            setChartData({ ...normalizedData, status: data.status })
            setCriticalSla(data.sla)

            if (data.status === DataStatus.Ready) {
              setFetchingInterval(DEFAULT_TIMER)
            }
            if (data.status === DataStatus.Recalculating) {
              setFetchingInterval(RECALCULATING_TIMER)
            }
            if (data.status === DataStatus.Initializing) {
              setFetchingInterval(INITIALIZING_TIMER)
            }

            if (data) {
              const { sla, recentValue, zoneColor } = data
              const currentSeverity = payload.section

              if (!!currentSeverity && !!data && data.values?.length > 0) {
                const healthDirection = getHealthDirection(zoneColor, data.recentValue, sla)
                const isGreen = checkIsGreen(healthDirection, recentValue, sla)
                setHealthDirection(healthDirection)

                if (isGreen) {
                  setCurrentSeverityStatus('healthy')
                } else {
                  setCurrentSeverityStatus('problematic')
                }
              }
            }

            return data
          } else {
            const { data }: { data: ChartData } = res.data
            data.status = normalizeMetricStatus(data.status)
            if (recalculatingCpiName === cpi?.name) {
              const isRecalculating = data.status === DataStatus.Recalculating
              setShowMessage(isRecalculating)
              if (!isRecalculating) setRecalculatingCpiName(null)
            }

            if (data.status === DataStatus.Initializing && !data.values) {
              setFetchingInterval(INITIALIZING_TIMER)
              const userInput = manifestData.ui.trend_chart.user_input

              const sectionSla = userInput.visualization_param.sections.find((n) => n.name === selectedSeverity)
              const sla =
                sectionSla && sectionSla.value ? sectionSla.value : userInput.visualization_param.identical_value ?? 0
              const stubValues: ChartDataValue[] = []
              let date = dayjs().format('YYYY-MM-DD')
              Array.from(Array(3).keys()).forEach((n) => {
                let d = date
                if (n !== 0) {
                  const c = dayjs(date).subtract(1, 'month')
                  d = dayjs(c).format('MM-DD-YYYY')
                }
                stubValues.push({
                  xValue: n === 0 ? date : dayjs(d).format('YYYY-MM-DD'),
                  yValue: 0,
                  denominator: 0,
                  numerator: 0
                })
                date = dayjs(d).format('MM-DD-YYYY')
              })
              const today = new Date().toISOString()
              const stubInitializingData: ChartData = {
                recentValue: 0,
                recentDate: today,
                status: DataStatus.Initializing,
                zoneColor: 'green',
                sla,
                updatedAt: today,
                values: stubValues
              }
              setChartData(stubInitializingData)

              return
            }

            const normalizedValues = data.values.map((value) => ({
              ...value,
              yValue: isNaN(value.yValue) ? 0 : value.yValue
            }))
            const normalizedData = {
              ...data,
              values: normalizedValues
            }

            if (!initialLoad) {
              const dPrediction = predictionData?.values[predictionData?.values.length - 1]

              const recentPerformanceDate = getUtcDateAndTime(data.recentDate).labelFormatDate
              const recentPerformanceDataPoint = data.values.find(
                (n) => n.xValue.slice(0, 10) === recentPerformanceDate
              )

              const defaultLastDayValue =
                manifestData.name === 'cpi997'
                  ? `${recentPerformanceDataPoint?.numerator ?? 0} / ${recentPerformanceDataPoint?.denominator ?? 0}`
                  : `${recentPerformanceDataPoint?.numerator ?? 0}`

              if (recentPerformanceDataPoint) {
                const lastDayDenominator =
                  healthUnit === 'percent'
                    ? recentPerformanceDataPoint.numerator
                    : recentPerformanceDataPoint.denominator ?? 0

                setDefaultLastDayProps({
                  value: defaultLastDayValue,
                  date: recentPerformanceDataPoint.xValue.slice(0, 10),
                  lastDayDenominator,
                  predictionValue: `${dPrediction?.yValue ?? 0}`,
                  predictionDate: dPrediction
                    ? dPrediction.xValue.slice(0, 10)
                    : recentPerformanceDataPoint.xValue.slice(0, 10)
                })
              } else {
                setDefaultLastDayProps(buildLastDayFallbackProps(normalizedData.values, dPrediction, manifestData.name))
              }
            }

            setChartData(normalizedData)
            setCriticalSla(data.sla)

            if (data.status === DataStatus.Ready) {
              setFetchingInterval(DEFAULT_TIMER)
            }
            if (data.status === DataStatus.Recalculating) {
              setFetchingInterval(RECALCULATING_TIMER)
            }
            if (data.status === DataStatus.Initializing) {
              setFetchingInterval(INITIALIZING_TIMER)
            }

            if (data) {
              const { sla, recentValue, zoneColor } = data
              const currentSeverity = payload.section

              if (!!currentSeverity && !!data && data.values?.length > 0) {
                const healthDirection = getHealthDirection(zoneColor, data.recentValue, sla)
                const isGreen = checkIsGreen(healthDirection, recentValue, sla)
                setHealthDirection(healthDirection)

                if (isGreen) {
                  setCurrentSeverityStatus('healthy')
                } else {
                  setCurrentSeverityStatus('problematic')
                }
              }
            }

            return data
          }
        }
      } catch (e) {
        console.error(e)
      } finally {
        setStripes(stripes ?? null)
      }
    },
    [cpi, predictionData, manifestData, initialLoad, isSnowflake, accountSettings]
  )

  useEffect(() => {
    if (labelDrawerOpen) {
      const tooltip = d3.select('.data-point-tooltip')
      tooltip.style('display', 'none')
    }
  }, [labelDrawerOpen])

  useEffect(() => {
    fetchInitialLabels().catch((e) => e)

    return () => {
      setInitialQueryLoaded(false)
    }
  }, [cpi])

  useEffect(() => {
    if (!cpi || (selectedDataSourceIds.length === 1 && selectedDataSourceIds[0] === '')) return

    const timePeriod = getPayloadTimePeriod(selectedTimePeriod)

    const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

    const payload: TrendsDataPayload = {
      section: resolveSectionName(selectedSeverity, manifestData) as CPISeverity,
      integration_ids: integrationIds,
      frequency: selectedFrequency
    }

    if (customTimePeriodValue) {
      const { dateFrom, dateTo } = extractMonth(customTimePeriodValue)
      const dateToTemp = new Date(dateTo).getTime()
      const today = new Date().getTime()
      const dateToFormatted = dateToTemp > today ? dayjs().format('YYYY-MM-DD') : dateTo
      payload.dateFrom = dateFrom
      payload.dateTo = dateToFormatted
    } else {
      payload.timePeriod = timePeriod
    }

    if (!hasFetchedInitially.current && predictionFetched) {
      fetchTrendsData(payload, stripes)
        .catch((e) => e)
        .finally(() => {
          setInitialLoad(true)
          hasFetchedInitially.current = true
        })
    }

    if (predictionFetched && hasFetchedInitially.current) {
      const refetchData = setInterval(() => {
        fetchTrendsData(payload, stripes).catch((e) => e)
      }, fetchingInterval)

      return () => {
        clearInterval(refetchData)
      }
    }
  }, [
    cpi,
    stripes,
    predictionFetched,
    manifestData,
    selectedDataSourceIds,
    customTimePeriodValue,
    selectedFrequency,
    selectedSeverity,
    selectedTimePeriod,
    fetchingInterval,
    initialLoad
  ])

  useEffect(() => {
    if (!labelDrawerOpen) {
      setMode('show-0')
      setEditLabel({} as any)
      setCustomDate({
        date: '',
        start: '',
        end: ''
      })
      setAppliedFilter({
        startDate: '',
        endDate: '',
        types: []
      })
    }
  }, [labelDrawerOpen])

  useEffect(() => {
    if (labelDrawerOpen) {
      setLoading(true)
      updateLabels()
    }
  }, [labelDrawerOpen, selectedFrequency, selectedTimePeriod])

  const fetchDefaultParamsForLastValue = useCallback(async () => {
    if (!cpi || selectedDataSourceIds.length === 0 || !manifestData) return

    try {
      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)
      const { date_range: dateRange, frequency } = manifestData.ui.trend_chart
      const defaultDateRange = adjustDefaultTimePeriod(dateRange.default, frequency.default as CPIFrequency)
      const defaultFrequency = frequency.default
      const defaultTimePeriod = getPayloadTimePeriod(defaultDateRange)
      // Scope the Off-SLA snapshot to the active SCOPING filters (severity, device type,
      // data source) while pinning the default frequency/time-period: per ONYXIA-8156 the
      // snapshot is yesterday's daily data and must NOT be affected by Time Period/Frequency.
      const defaultPayload: TrendsDataPayload = {
        section: resolveSectionName(selectedSeverity, manifestData) as CPISeverity,
        integration_ids: integrationIds,
        frequency: defaultFrequency as CPIFrequency,
        timePeriod: defaultTimePeriod
      }
      if (selectedType && selectedType.toLowerCase() !== 'all') {
        defaultPayload.type = selectedType.toLowerCase()
      }

      let res: AxiosResponse<any, any> | null = null

      if (isSnowflake && cpi.configuration) {
        const instanceNames =
          integrationIds.length > 0
            ? integrationIds
            : cpi.configuration?.integrations.filter((c) => !c.deleted).map((c) => c.instanceName)

        const snowflakePayload = {
          metric_request: {
            section: defaultPayload.section,
            frequency: defaultPayload.frequency,
            instance_names: instanceNames,
            time_period: defaultPayload.timePeriod
          }
        }
        res = await axios.post(`/api/v4/metrics/${cpi.name}/timeseries?trends-timeseries-default`, snowflakePayload)
      } else {
        res = await axios.post(`/api/v3/cpis/${cpi.name}/data`, defaultPayload)
      }

      if (res && res.status.toString().includes('20')) {
        const data: ChartData | SnowflakeChartData = isSnowflake ? res.data : res.data.data
        data.status = normalizeMetricStatus(data.status)

        if (data && data.values && data.values.length > 0) {
          // Calculate lastValue from the default params data
          let computedLastValue: number | null
          if (instanceOfSnowflake(data)) {
            /* A withheld recent point means no eligible finding; the series must not stand in for it. */
            const recentPoint = data.recentPoint
            if (!recentPoint) {
              computedLastValue = null
            } else if (recentPoint.xValue.slice(0, 10) === new Date().toISOString().slice(0, 10)) {
              const yesterday = data.values[data.values.length - 2]
              computedLastValue = Math.round(yesterday?.yValue ?? 0)
            } else {
              computedLastValue = Math.round(recentPoint.yValue)
            }
          } else {
            computedLastValue = Math.round((data as ChartData)?.recentValue || 0)
          }

          const manifestUnit = manifestData?.ui.trend_chart.user_input.visualization_param.unit
          const unitText = formatPerformanceUnit(manifestUnit)
          const lastValueHtml = computedLastValue == null ? '' : `${computedLastValue}<span>${unitText}</span>`

          // Calculate other default values
          const healthUnit = manifestData.ui.trend_chart.user_input.visualization_param.unit
          const values = data.values.map((c) => ({ ...c, xValue: c.xValue.slice(0, 10) }))
          const recentPerformanceDate = getUtcDateAndTime(
            instanceOfSnowflake(data)
              ? data.recentPoint?.xValue ?? data.values[data.values.length - 1].xValue
              : (data as ChartData).recentDate
          ).labelFormatDate
          const recentPerformanceDataPoint = values.find((n) => n.xValue.slice(0, 10) === recentPerformanceDate)

          const defaultLastDayValue =
            manifestData.name === 'cpi997'
              ? `${recentPerformanceDataPoint?.numerator ?? 0}/${recentPerformanceDataPoint?.denominator ?? 0}`
              : `${recentPerformanceDataPoint?.numerator ?? 0}`

          let thisLastDayDenominator: string
          let dateStr: string

          if (recentPerformanceDataPoint) {
            thisLastDayDenominator =
              manifestData.name === 'cpi997'
                ? defaultLastDayValue
                : healthUnit === 'percent'
                ? `${recentPerformanceDataPoint.numerator}`
                : `${recentPerformanceDataPoint.denominator ?? 0}`
            dateStr = recentPerformanceDataPoint.xValue.slice(0, 10)
          } else {
            const defaultLastDay = data.values[data.values.length - 1]
            dateStr = defaultLastDay.xValue.slice(0, 10)
            thisLastDayDenominator = `${(defaultLastDay.numerator || defaultLastDay.denominator) ?? 0}`
          }

          const months = [
            'January',
            'February',
            'March',
            'April',
            'May',
            'June',
            'July',
            'August',
            'September',
            'October',
            'November',
            'December'
          ]
          // Parse date string directly to avoid timezone conversion
          const [yearStr, monthStr, dayStr] = dateStr.split('-')
          const month = parseInt(monthStr, 10) - 1 // 0-indexed for months array
          const year = parseInt(yearStr, 10)
          const thisMonth = months[month]
          const formattedDate = `${monthStr}/${dayStr}/${yearStr}`

          setDefaultRecentValueDate({
            date: defaultFrequency === CPIFrequency.Monthly ? `${thisMonth} ${year}` : formattedDate,
            value: defaultLastDayValue,
            denominator: thisLastDayDenominator,
            lastValue: lastValueHtml
          })
        }
      }
    } catch (e) {
      console.error('Error fetching default params for lastValue:', e)
    }
  }, [
    cpi,
    manifestData,
    isSnowflake,
    selectedDataSourceIds,
    dataSourceOptions,
    selectedSeverity,
    selectedType,
    setDefaultRecentValueDate
  ])

  useEffect(() => {
    if (trendsRefetchTrigger > 0 && cpi && selectedDataSourceIds.length > 0 && manifestData) {
      const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

      const payload: TrendsDataPayload = {
        section: selectedSeverity.toLowerCase() as CPISeverity,
        integration_ids: integrationIds,
        frequency: selectedFrequency
      }

      if (customTimePeriodValue) {
        const { dateFrom, dateTo } = extractMonth(customTimePeriodValue)
        const dateToTemp = new Date(dateTo).getTime()
        const today = new Date().getTime()
        const dateToFormatted = dateToTemp > today ? dayjs().format('YYYY-MM-DD') : dateTo
        payload.dateFrom = dateFrom
        payload.dateTo = dateToFormatted
      } else {
        payload.timePeriod = timePeriod
      }

      // Fetch with current filters for the chart
      fetchTrendsData(payload, stripes).catch((e) => e)

      // Also fetch with default parameters for lastValue calculation
      fetchDefaultParamsForLastValue()
    }
  }, [trendsRefetchTrigger])

  // Load/refresh the Last Day Value whenever the chart transitions INTO Ready — including the
  // first load (prevStatus null), where an active CPI's chart often goes straight to Ready with
  // no intermediate non-Ready status. Without covering that initial transition the Off-SLA
  // snapshot is never fetched on mount and the card is stuck at the default 0 until a scoping
  // filter changes (ONYXIA-8156).
  useEffect(() => {
    const currentStatus = chartData?.status
    const prevStatus = prevChartStatusRef.current

    if (currentStatus === DataStatus.Ready && prevStatus !== DataStatus.Ready) {
      fetchDefaultParamsForLastValue()
    }

    if (currentStatus) {
      prevChartStatusRef.current = currentStatus
    }
  }, [chartData?.status, fetchDefaultParamsForLastValue])

  // Refetch the Off-SLA snapshot when a SCOPING filter (severity, device type, data source)
  // changes. Time Period/Frequency are intentionally excluded — the snapshot always reflects
  // yesterday's data (ONYXIA-8156). The initial snapshot is loaded by the status->Ready effect
  // above, so skip the first run here to avoid a duplicate fetch on mount.
  const skipFirstSnapshotRefetch = useRef(true)
  useEffect(() => {
    if (skipFirstSnapshotRefetch.current) {
      skipFirstSnapshotRefetch.current = false
      return
    }
    if (cpi && selectedDataSourceIds.length > 0 && manifestData) {
      fetchDefaultParamsForLastValue()
    }
  }, [selectedSeverity, selectedType, selectedDataSourceIds])

  const buildTrendsChart = useCallback(
    (data: ChartData, yValues: number[]) => {
      if (!manifestData) return
      /* Zero in-window points is renderEmptyChart's job, not this builder's. */
      if (!data.values.length) return
      const status = data.status

      const isNoSeveritiesCpi =
        !manifestData?.ui.trend_chart.user_input.formula_params ||
        (manifestData?.ui.trend_chart.user_input.visualization_param.identical_section_values &&
          manifestData?.ui.trend_chart.user_input.visualization_param.sections.length < 2)
      const healthUnit = manifestData?.ui.trend_chart.user_input.visualization_param.unit
      const tooltipUnit = manifestData?.ui.trend_chart.user_input.visualization_param.tooltip_unit
      const dataFirstValue = getUtcDateAndTime(data.values[0].xValue || '').labelFormatDate

      const svg = d3.select(svgRef.current)
      const tooltip = d3.select('.data-point-tooltip')
      const everything = svg.selectAll('*')
      everything.remove()

      const filteredData = data.values
      let groupedByWeeks: ChartDataExtended | null = null
      if (selectedTimePeriod === CPITimePeriod.Month) {
        groupedByWeeks = groupByWeeks(data)
      }
      if (selectedFrequency === CPIFrequency.Weekly && selectedTimePeriod !== CPITimePeriod.Month) {
        groupedByWeeks = addStartEndWeekly(data)
      }
      const dataStartsAt = getDataStartsAt(dataFirstValue, groupedByWeeks, selectedFrequency)

      const maxYScaleValue = Number(d3.max(yValues))
      const getYAxisDomain = (): [number, number] => {
        const defaultPercent: [number, number] = [0, 100]
        const nonPercentRange: [number, number] = [0, maxYScaleValue * 2]

        if (healthUnit === 'percent') {
          return defaultPercent
        }
        if (maxYScaleValue === 0 || data.status !== DataStatus.Ready) {
          return [0, 30]
        }
        return nonPercentRange
      }
      const yAxisDomain = getYAxisDomain()
      let uniqueMonths = findUniqueMonths({
        filteredData,
        groupedByWeeks,
        selectedTimePeriod
      })
      if (status === DataStatus.Initializing) {
        uniqueMonths = findUniqueMonthsInitializing(filteredData, selectedTimePeriod, data)
      } else {
        /*
         * The response carries only the days a source has, so widen the axis from the period itself.
         * A metric mid-recompute answers with real dates like any other, so it widens too; only
         * Initializing, which has no real dates yet, builds its stripes another way.
         * */
        uniqueMonths = padStripesToWindow(
          uniqueMonths,
          findWindowStripes({
            selectedTimePeriod,
            selectedFrequency,
            customTimePeriodValue,
            lastDataDate: filteredData[filteredData.length - 1]?.xValue
          })
        )
      }
      setCurrentUniqueMonths(uniqueMonths)
      const stripeWidth = width / uniqueMonths.length
      const notReadyMiddle = yAxisDomain[1] / 10
      const positionsData: ChartDataValue[] =
        status === DataStatus.Ready
          ? filteredData
          : filteredData.map((n) => ({
              ...n,
              yValue: notReadyMiddle
            }))

      const [pathStr, positions, isolatedPositions] = getPathAndPositions({
        uniqueMonths,
        stripeWidth,
        height: CHART_HEIGHT,
        filteredData: positionsData,
        groupedByWeeks,
        selectedTimePeriod,
        selectedFrequency,
        yAxisDomain,
        dataStatus: status,
        chartHeightStart
      })
      setCurrentPositions(positions)

      setChartDateRange(
        getAxisDateRange(
          uniqueMonths,
          selectedTimePeriod,
          selectedFrequency,
          groupedByWeeks ? groupedByWeeks.values : filteredData
        )
      )

      const xScaleGroup = d3.scaleBand().domain(uniqueMonths).range([0, width]).padding(0)
      const yScale = d3.scaleLinear().domain(yAxisDomain).range([CHART_HEIGHT, chartHeightStart]).nice()

      const yLine = getYLine(chartData, maxValueY)
      const healthBorder = yScale(yLine)

      let dataDates: LabelCount[] = []
      if (labelsCount && labelsCount.labels_counts.length > 0) {
        dataDates = labelsCount.labels_counts.map((label) => ({ ...label, date: label.date.slice(0, 10) }))
      }
      const pinnedLabels = labelsCount?.pinned_labels

      let thisVisibleLabels = []

      if (selectedTimePeriod !== CPITimePeriod.Week) {
        const scaledLabels = findVisibleLabelsXPositionMonthly(
          labelsCount?.pinned_labels || [],
          width,
          uniqueMonths,
          selectedTimePeriod
        )
        setVisibleLabels(scaledLabels)
        thisVisibleLabels = scaledLabels
      } else {
        const scaledLabels = findVisibleLabelsXPosition(labelsCount?.pinned_labels || [], positions, width)
        setVisibleLabels(scaledLabels)
        thisVisibleLabels = scaledLabels
      }

      appendTrendsChartElements({
        svg,
        tooltip,
        xScaleGroup,
        sectionsDisplayName: manifestData?.ui.trend_chart.user_input.visualization_param.sections_display_name,
        sectionsDisplayNameSingular:
          manifestData?.ui.trend_chart.user_input.visualization_param.sections_singular_display_name,
        isNoSeveritiesCpi,
        yScale,
        pathStr,
        isolatedPositions,
        uniqueMonths,
        filteredData,
        groupedByWeeks,
        data,
        pinnedLabels,
        dataDates,
        selectedTimePeriod,
        selectedFrequency,
        selectedSeverity,
        width,
        height: CHART_HEIGHT,
        chartHeightStart,
        stripeWidth,
        healthUnit,
        healthBorder,
        dataStartsAt,
        setDrawerOpen: setLabelDrawerOpen,
        setMode,
        setCustomDate,
        setRedirectToMode,
        chartData,
        visibleLabels: thisVisibleLabels,
        areas: manifestData.ui.trend_chart.areas
      })

      /*
       * Mouse Move for data point hover tooltip
       * */
      /*
       * How near the cursor must be to a point to be reporting on that point at all. A lone point
       * has no neighbour to measure that against, so it falls back to one stripe of the window, not
       * of the drawn domain: a sparse response can collapse that to stripes wide enough to reach
       * across the chart.
       * */
      const windowStripes = findWindowStripes({
        selectedTimePeriod,
        selectedFrequency,
        customTimePeriodValue,
        lastDataDate: filteredData[filteredData.length - 1]?.xValue
      })
      const pointProximityLimit = getPointProximityLimit(
        positions,
        stripeWidth,
        windowStripes.length ? width / windowStripes.length : width
      )
      svg.on('mousemove', (event) => {
        if (status !== DataStatus.Ready || !positions.length) return

        const [mouseX, mouseY] = d3.pointer(event)
        const dataPoint = findNearestXIndex(positions, mouseX, healthUnit, pointProximityLimit)
        const barHeight = CHART_HEIGHT + margin.top + margin.bottom
        const diff = barHeight - xAxisHeight
        const isHoveringXAxis = mouseY >= diff
        const matchingDataPoint =
          selectedFrequency === CPIFrequency.Weekly && groupedByWeeks
            ? groupedByWeeks.values[dataPoint?.index || 0]
            : filteredData[dataPoint?.index || 0]

        if (mouseX > width || mouseY > CHART_HEIGHT || mouseY < CHART_HEIGHT) {
          const addCustomEventButton = svg.select('g.add-event-button')
          addCustomEventButton.style('display', 'block')
        }

        /*
         * A stripe the response never filled contributes no hover point, so the add-label button
         * would otherwise stay parked on the nearest stripe that has one. Put it under the cursor.
         * */
        const hoveredStripe = Math.min(Math.max(Math.floor(mouseX / stripeWidth), 0), uniqueMonths.length - 1)
        const emptyStripe = !positions.some((p) => p.monthIndex === hoveredStripe)

        /* Cursor is in a stretch with no point; a neighbour's reading would be misattributed to it. */
        if (!dataPoint) {
          hideHoverElements({ svg, tooltip })
          if (emptyStripe) positionAddEventButton({ svg, stripeWidth, index: hoveredStripe })
          return
        }
        onMouseMove({
          cpi: manifestData.name,
          svg,
          tooltip,
          event,
          dataPoint,
          d: {
            ...matchingDataPoint,
            yValue: Math.round(matchingDataPoint.yValue)
          },
          selectedTimePeriod,
          selectedFrequency,
          selectedSeverity,
          stripeWidth,
          isHoveringXAxis,
          healthUnit,
          tooltipUnit,
          isNoSeveritiesCpi,
          dataStartsAt,
          mouseX,
          mouseY,
          addButtonIndex: emptyStripe ? hoveredStripe : undefined
        })
      })

      raiseElements(svg)
    },
    [
      manifestData,
      width,
      margin,
      customLabels,
      labelsCount,
      labelDrawerOpen,
      selectedTimePeriod,
      customTimePeriodValue,
      selectedFrequency,
      selectedSeverity,
      isSnowflake
    ]
  )

  const buildPredictionChart = (chartData: PredictionChartData, yValues: number[]) => {
    if (!manifestData) return
    if (!chartData.values.length) return
    const isNoSeveritiesCpi =
      !manifestData?.ui.trend_chart.user_input.formula_params ||
      (manifestData?.ui.trend_chart.user_input.visualization_param.identical_section_values &&
        manifestData?.ui.trend_chart.user_input.visualization_param.sections.length < 2)
    const healthUnit = manifestData?.ui.trend_chart.user_input.visualization_param.unit
    const tooltipUnit = manifestData?.ui.trend_chart.user_input.visualization_param.tooltip_unit
    const dataFirstValue = getUtcDateAndTime(chartData.values[0].xValue || '').labelFormatDate

    const svg = d3.select(svgRef.current)
    const tooltip = d3.select('.data-point-tooltip')
    const everything = svg.selectAll('*')
    everything.remove()

    const data = chartData

    /* ONYXIA-3546 */
    const filteredData = data.values
    const dataStartsAt = getDataStartsAt(dataFirstValue, null, selectedFrequency)

    const maxYScaleValue = Number(d3.max(yValues))
    const getYAxisDomain = (): [number, number] => {
      const defaultPercent: [number, number] = [0, 100]
      const nonPercentRange: [number, number] = [0, maxYScaleValue * 2]

      if (healthUnit === 'percent') {
        return defaultPercent
      }
      if (maxYScaleValue === 0 || data.status !== DataStatus.Ready) {
        return [0, 30]
      }
      return nonPercentRange
    }
    const yAxisDomain = getYAxisDomain()
    const uniqueMonths = findUniqueMonthsPredictions({ filteredData })
    const stripeWidth = width / uniqueMonths.length

    const [pathStr, positions, pathStrPrediction] = getPathAndPositionsPredictions({
      uniqueMonths,
      stripeWidth,
      height: CHART_HEIGHT,
      filteredData: filteredData as any,
      selectedTimePeriod: CPITimePeriod.ThreeMonths,
      selectedFrequency: CPIFrequency.Daily,
      yAxisDomain,
      dataStatus: data.status,
      chartHeightStart,
      groupedByWeeks: null
    })

    const xScaleGroup = d3.scaleBand().domain(uniqueMonths).range([0, width]).padding(0)
    const yScale = d3.scaleLinear().domain(yAxisDomain).range([CHART_HEIGHT, chartHeightStart]).nice()
    const yLine = getYLine(chartData as any, maxValueY)
    const healthBorder = yScale(yLine)

    /*
     * Mouse Move for data point hover tooltip
     * */
    /* How near the cursor must be to a point to be reporting on that point at all. */
    const pointProximityLimit = getPointProximityLimit(positions, stripeWidth)
    svg.on('mousemove', (event) => {
      if (data.status !== DataStatus.Ready || !positions.length) return

      const [mouseX, mouseY] = d3.pointer(event)
      const dataPoint = findNearestXIndex(positions, mouseX, undefined, pointProximityLimit)
      const barHeight = CHART_HEIGHT + margin.top + margin.bottom
      const diff = barHeight - xAxisHeight
      const isHoveringXAxis = mouseY >= diff
      const matchingDataPoint = filteredData[dataPoint?.index || 0]

      if (mouseX > width || mouseY > CHART_HEIGHT || mouseY < CHART_HEIGHT) {
        const addCustomEventButton = svg.select('g.add-event-button')
        addCustomEventButton.style('display', 'block')
      }

      if (!dataPoint) {
        hideHoverElements({ svg, tooltip })
        return
      }
      onMouseMove({
        cpi: manifestData.name,
        svg,
        tooltip,
        event,
        dataPoint,
        d: matchingDataPoint,
        selectedTimePeriod,
        selectedFrequency,
        selectedSeverity,
        stripeWidth,
        isHoveringXAxis,
        healthUnit,
        tooltipUnit,
        isNoSeveritiesCpi,
        dataStartsAt,
        mouseX,
        mouseY,
        predictionTooltip: true
      })
    })

    appendPredictionTrendsChartElements({
      svg,
      tooltip,
      xScaleGroup,
      sectionsDisplayName: manifestData.ui.trend_chart.user_input.visualization_param.sections_display_name,
      sectionsDisplayNameSingular:
        manifestData.ui.trend_chart.user_input.visualization_param.sections_singular_display_name,
      isNoSeveritiesCpi,
      yScale,
      pathStrPrediction,
      pathStr,
      getAreaFill,
      uniqueMonths,
      filteredData,
      data: data as any,
      pinnedLabels: [],
      dataDates: [],
      selectedTimePeriod,
      selectedFrequency,
      selectedSeverity,
      width,
      height: CHART_HEIGHT,
      chartHeightStart,
      stripeWidth,
      healthUnit,
      healthBorder,
      getAreaStroke,
      getLineColor,
      chartData: chartData as any,
      visibleLabels: [],
      hideEventIndicators: true
    })

    raiseElements(svg)
  }

  const buildOpenClosedChart = (chartData: ChartData, timeRange: number, noData: boolean) => {
    if (!manifestData || !chartData.values.length) return
    const tooltipUnit = manifestData.ui.trend_chart.user_input.visualization_param.tooltip_unit
    const healthUnit = manifestData.ui.trend_chart.user_input.visualization_param.unit
    const firstXValue = chartData.values[0].xValue
    const dataStartsAt = (typeof firstXValue === 'string' ? firstXValue : new Date(firstXValue).toISOString()).slice(
      0,
      10
    )
    const max = Math.max(...chartData.values.map((n) => Math.abs(n.yValue)))

    const svg = d3.select(svgRef.current)
    const tooltip = d3.select('.data-point-tooltip')
    const everything = svg.selectAll('*')
    everything.remove()

    const data: ChartData | SnowflakeChartData = {
      ...chartData,
      values: chartData.values.map((n) => ({
        ...n,
        xValue: (typeof n.xValue === 'string' ? n.xValue : new Date(n.xValue).toISOString()).slice(0, 10)
      }))
    }
    const filteredData = filterData(timeRange, data)
    let groupedByWeeks: ChartDataExtended | null = null
    if (selectedTimePeriod === CPITimePeriod.Month) {
      groupedByWeeks = groupByWeeks(data)
    }
    if (selectedFrequency === CPIFrequency.Weekly && selectedTimePeriod !== CPITimePeriod.Month) {
      groupedByWeeks = addStartEndWeekly(data)
    }

    let uniqueMonths = findUniqueMonths({
      filteredData,
      selectedTimePeriod,
      groupedByWeeks
    })
    /* The response carries only the days a source has, so widen the axis from the period itself. */
    uniqueMonths = padStripesToWindow(
      uniqueMonths,
      findWindowStripes({
        selectedTimePeriod,
        selectedFrequency,
        customTimePeriodValue,
        lastDataDate: filteredData[filteredData.length - 1]?.xValue
      })
    )
    const stripeWidth = width / uniqueMonths.length
    const yDomain = max === 0 ? [-100, 100] : [-max, max]

    const [pathStr, positions, , isolatedPositions] = getPathAndPositionsOC({
      uniqueMonths,
      stripeWidth,
      height: CHART_HEIGHT,
      filteredData,
      groupedByWeeks,
      selectedTimePeriod,
      selectedFrequency,
      yAxisDomain: yDomain as [number, number],
      isReady: chartData.status === DataStatus.Ready,
      chartHeightStart
    })

    const yScale = d3.scaleLinear().domain(yDomain).range([CHART_HEIGHT, chartHeightStart]).nice()
    const xScaleGroup = d3.scaleBand().domain(uniqueMonths).range([0, width]).padding(0)
    const yLine = getYLine(chartData, maxValueY)
    const healthBorder = yScale(yLine)

    let dataDates: LabelCount[] = []
    if (labelsCount && labelsCount.labels_counts.length > 0) {
      dataDates = labelsCount.labels_counts.map((label) => ({ ...label, date: label.date.slice(0, 10) }))
    }
    const pinnedLabels = labelsCount?.pinned_labels

    /*
     * Mouse Move for data point tooltip
     * */
    /*
     * How near the cursor must be to a point to be reporting on that point at all. A lone point
     * has no neighbour to measure that against, so it falls back to one stripe of the window, not
     * of the drawn domain: a sparse response can collapse that to stripes wide enough to reach
     * across the chart.
     * */
    const windowStripes = findWindowStripes({
      selectedTimePeriod,
      selectedFrequency,
      customTimePeriodValue,
      lastDataDate: filteredData[filteredData.length - 1]?.xValue
    })
    const pointProximityLimit = getPointProximityLimit(
      positions,
      stripeWidth,
      windowStripes.length ? width / windowStripes.length : width
    )
    svg.on('mousemove', (event) => {
      if (chartData.status !== DataStatus.Ready || !positions.length) return

      const [mouseX, mouseY] = d3.pointer(event)
      const dataPoint = findNearestXIndex(positions, mouseX, undefined, pointProximityLimit)
      const barHeight = CHART_HEIGHT + margin.top + margin.bottom
      const diff = barHeight - xAxisHeight
      const isHoveringXAxis = mouseY >= diff
      const matchingDataPoint =
        selectedFrequency === CPIFrequency.Weekly && groupedByWeeks
          ? groupedByWeeks.values[dataPoint?.index || 0]
          : filteredData[dataPoint?.index || 0]

      if (mouseX > width || mouseY > CHART_HEIGHT || mouseY < CHART_HEIGHT) {
        const addCustomEventButton = svg.select('g.add-event-button')
        addCustomEventButton.style('display', 'block')
      }

      /*
       * A stripe the response never filled contributes no hover point, so the add-label button would
       * otherwise stay parked on the nearest stripe that has one. Put it under the cursor.
       * */
      const hoveredStripe = Math.min(Math.max(Math.floor(mouseX / stripeWidth), 0), uniqueMonths.length - 1)
      const emptyStripe = !positions.some((p) => p.monthIndex === hoveredStripe)

      if (!dataPoint || noData) {
        hideHoverElements({ svg, tooltip })
        if (emptyStripe && !noData) positionAddEventButton({ svg, stripeWidth, index: hoveredStripe })
        return
      }
      onMouseMove({
        svg,
        tooltip,
        event,
        dataPoint,
        d: matchingDataPoint,
        selectedTimePeriod,
        selectedFrequency,
        selectedSeverity,
        stripeWidth,
        isHoveringXAxis,
        healthUnit,
        tooltipUnit,
        isNoSeveritiesCpi: false,
        dataStartsAt,
        ocTooltip: true,
        mouseX,
        mouseY,
        addButtonIndex: emptyStripe ? hoveredStripe : undefined
      })
    })

    let thisVisibleLabels = []

    if (selectedTimePeriod !== CPITimePeriod.Week) {
      const scaledLabels = findVisibleLabelsXPositionMonthly(
        labelsCount?.pinned_labels || [],
        width,
        uniqueMonths,
        selectedTimePeriod
      )
      setVisibleLabels(scaledLabels)
      thisVisibleLabels = scaledLabels
    } else {
      const scaledLabels = findVisibleLabelsXPosition(
        labelsCount?.pinned_labels || [],
        positions.map((n) => ({
          ...n,
          date: dayjs(n.date).format(dateFormat.customLabel)
        })),
        width
      )
      setVisibleLabels(scaledLabels)
      thisVisibleLabels = scaledLabels
    }

    appendTrendsChartOCElements({
      svg,
      data,
      tooltip,
      xScaleGroup,
      sectionsDisplayName: manifestData.ui.trend_chart.user_input.visualization_param.sections_display_name,
      sectionsDisplayNameSingular:
        manifestData.ui.trend_chart.user_input.visualization_param.sections_singular_display_name,
      yScale,
      uniqueMonths,
      selectedTimePeriod,
      selectedFrequency,
      selectedSeverity,
      healthBorder,
      filteredData,
      dataStartsAt,
      width,
      height: CHART_HEIGHT,
      chartHeightStart,
      healthUnit,
      noData,
      setRedirectToMode,
      stripeWidth,
      setCustomDate,
      setDrawerOpen: setLabelDrawerOpen,
      setMode,
      getLineColor,
      getAreaStroke,
      pathStr,
      isolatedPositions,
      pinnedLabels,
      dataDates,
      groupedByWeeks,
      chartData,
      visibleLabels: thisVisibleLabels
    })

    if (noData) {
      removeElements(svg, tooltip)
    } else {
      raiseElements(svg)
    }
  }

  /* Wipe the previous build: its mousemove closures hold stale positions and its line would persist. */
  const renderEmptyChart = useCallback(() => {
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.on('mousemove', null)
    d3.select('.data-point-tooltip').style('display', 'none')
    setCurrentPositions([])
    setCurrentUniqueMonths([])
    setVisibleLabels([])
    setChartDateRange({} as DateRangeI)
  }, [])

  useEffect(() => {
    if (!chartData || !manifestData) {
      setRenderingChart(false)
      return
    }

    /* Every builder below needs at least one point; an empty answer clears the canvas instead. */
    const activeValues = predictionModalOpen && predictionData ? predictionData.values : chartData.values
    if (!activeValues.length) {
      setMaxValueY(0)
      renderEmptyChart()
      setRenderingChart(false)
      return
    }

    const dataMaxValue = Math.max(...chartData.values.map((e: any) => (isNaN(e.yValue) ? 0 : e.yValue)))
    setMaxValueY(dataMaxValue)

    if (predictionModalOpen && predictionData) {
      const yValues = predictionData.values.map((d) => d.yValue)
      buildPredictionChart(predictionData as any, yValues)
    } else if (manifestData.display_name === 'CPI-007') {
      setMaxValueY(dataMaxValue)
      let noData = false
      if (!chartData.values) {
        noData = true
      }
      /*
       * Months of data to keep. This has to match the window the axis now spans, and the extra one
       * it used to add left 3M covering four months of data and reading as four stripes wide.
       * */
      const months =
        stripes !== null && typeof stripes !== 'undefined'
          ? stripes
          : selectedTimePeriod === CPITimePeriod.PastYear
          ? 12
          : parseInt(selectedTimePeriod.split('')[0], 10)
      /*
       * The dates go through as the strings the response sent, the way every other chart takes them.
       * Reading them into Date objects first read them as local midnight, and the builder's trip back
       * out through toISOString then landed a day earlier for anyone east of UTC.
       * */
      buildOpenClosedChart(chartData as ChartData, months, noData)
    } else {
      chartData.values = chartData.values.map((e) => {
        const num = isNaN(e.yValue) ? 0 : e.yValue

        return {
          ...e,
          yValue: dataMaxValue === 0 ? 0 : num,
          xValue: (typeof e.xValue === 'string' ? e.xValue : new Date(e.xValue).toISOString()).slice(0, 10)
        }
      })

      const yValues = chartData.values.map((d) => (isNaN(d.yValue) ? 0 : d.yValue))

      buildTrendsChart(chartData as ChartData, yValues)
    }
    setRenderingChart(false)
  }, [
    chartData,
    predictionModalOpen,
    predictionData,
    customLabels,
    customLabelsCount,
    width,
    location,
    themeMode,
    renderEmptyChart
  ])

  // Position the HTML SLA chip overlay from the invisible anchor D3 draws each render.
  useEffect(() => {
    const anchor = svgRef.current?.querySelector('.sla-chip-anchor')
    if (!anchor) {
      setSlaChip(null)
      return
    }
    setSlaChip({
      x: Number(anchor.getAttribute('x')),
      y: Number(anchor.getAttribute('y')),
      w: Number(anchor.getAttribute('width')),
      h: Number(anchor.getAttribute('height')),
      text: anchor.getAttribute('data-text') || ''
    })
  }, [chartData, predictionModalOpen, predictionData, customLabels, customLabelsCount, width, location, themeMode])

  const filterLabelsData = (filters: any) => {
    if (!filters) {
      return setAppliedFilter({
        startDate: '',
        endDate: '',
        types: []
      })
    }

    const filterData = {} as CustomLabelsListI
    Object.keys(customLabels).forEach((item) => {
      if (item >= filters.startDate && item <= filters.endDate) {
        if (filters.types.length) {
          const list = customLabels[item].filter((i: { action: string }) => filters.types.includes(i.action))
          if (list.length) filterData[item] = list
        } else {
          filterData[item] = customLabels[item]
        }
      }
    })
    setAppliedFilter(filters)
  }

  const fetchCustomLabelsCount = useCallback(
    async (payload: CustomLabelsDataPayload, thisTimePeriod: CPITimePeriod = selectedTimePeriod) => {
      try {
        const resp = await axios.post(`/api/v3/labels/cpis/${cpi?.name}/count?fetch-labels-count`, payload)
        const totalCount = calculateLabelCount(resp.data.data.labels_counts, thisTimePeriod, customTimePeriodValue)
        setCustomLabelsCount(resp.data.data)
        setLabelsCount({
          ...resp.data.data,
          pinned_labels: resp.data.data.pinned_labels.slice(0, MAX_PINNED_LABELS_COUNT)
        })
        setLabelCount(totalCount)

        if (thisTimePeriod !== CPITimePeriod.Week) {
          const scaledLabels = findVisibleLabelsXPositionMonthly(
            resp.data.data.pinned_labels || [],
            width,
            currentUniqueMonths,
            thisTimePeriod
          )
          setVisibleLabels(scaledLabels)
        } else {
          const scaledLabels = findVisibleLabelsXPosition(resp.data.data.pinned_labels || [], currentPositions, width)
          setVisibleLabels(scaledLabels)
        }
      } catch (e) {
        console.error(e)
      }
    },
    [cpi, currentUniqueMonths, selectedTimePeriod, currentPositions, width, customTimePeriodValue]
  )

  const fetchCustomLabels = useCallback(
    async (payload: CustomLabelsDataPayload): Promise<CustomLabelsListI> => {
      try {
        const resp = await axios.post(`/api/v3/labels/cpis/${cpi?.name}?fetch-custom-labels`, payload)
        setCustomLabels(resp.data.data)
        return resp.data.data
      } catch (e) {
        console.error(e)
        return {}
      }
    },
    [cpi, customDate.start, customDate.end]
  )

  const buildLabelsPayload = (payload?: CustomLabelsDataPayload, ignoreCustomPeriod = false) => {
    const timePeriod = getPayloadTimePeriod(selectedTimePeriod)
    const thisPayload: any = payload || {
      time_period: timePeriod,
      frequency: selectedFrequency
    }

    // Only apply custom time period filtering if we're not ignoring it
    if (customTimePeriodValue && !ignoreCustomPeriod) {
      const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const parts = customTimePeriodValue.split('-')
      const sub1 = parts[0].split(" '")
      const sub2 = parts[1].split(" '")
      const startMonth = String(shortMonths.findIndex((n) => n === sub1[0]) + 1).padStart(2, '0')
      const startYear = sub1.length > 1 ? `20${sub1[1]}` : `20${sub2[1]}`
      const endMonth = String(shortMonths.findIndex((n) => n === sub2[0]) + 1).padStart(2, '0')
      const endYear = `20${sub2[1]}`
      const daysInEndMonth = dayjs(`${endYear}-${endMonth}-01`).daysInMonth()
      thisPayload.date_from = dayjs(`${startYear}-${startMonth}-01`).format(dateFormat.customLabel)
      thisPayload.date_to = dayjs(`${endYear}-${endMonth}-${daysInEndMonth}`).format(dateFormat.customLabel)
      delete thisPayload.time_period
    }

    return thisPayload
  }

  const countLabelsFromList = (labels: CustomLabelsListI): number =>
    Object.values(labels).reduce((total, list) => total + list.length, 0)

  const updateLabels = async (payload?: CustomLabelsDataPayload, ignoreCustomPeriod = false) => {
    const thisPayload = buildLabelsPayload(payload, ignoreCustomPeriod)

    try {
      const [, labels] = await Promise.all([fetchCustomLabelsCount(thisPayload), fetchCustomLabels(thisPayload)])
      setLabelCount(countLabelsFromList(labels))
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  /**
   * After a label mutation (create/edit/delete), the backend may not reflect
   * the change immediately due to eventual consistency. This uses the current
   * local label count as baseline and retries fetching until the count changes,
   * guaranteeing both the drawer list and chart indicators update.
   */
  const updateLabelsAfterMutation = async (payload?: CustomLabelsDataPayload, ignoreCustomPeriod = false) => {
    const thisPayload = buildLabelsPayload(payload, ignoreCustomPeriod)
    const prevTotal = labelCount
    const maxRetries = 5
    const retryDelay = 800

    try {
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        if (attempt > 0) {
          await new Promise((resolve) => setTimeout(resolve, retryDelay))
        }

        const [, labels] = await Promise.all([fetchCustomLabelsCount(thisPayload), fetchCustomLabels(thisPayload)])

        const newTotal = countLabelsFromList(labels)

        if (newTotal !== prevTotal) {
          // Re-fetch one more time to ensure both list and count are fully in sync
          const [, finalLabels] = await Promise.all([
            fetchCustomLabelsCount(thisPayload),
            fetchCustomLabels(thisPayload)
          ])
          setLabelCount(countLabelsFromList(finalLabels))
          break
        }
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const deleteLabelCallback = async (labelId: string) => {
    try {
      await updateLabelsAfterMutation()
      await setMode('show-0')
      const prevVisibleLabels = [...visibleLabels]
      const found = prevVisibleLabels.findIndex((n) => n.id === labelId)
      if (found !== -1) {
        prevVisibleLabels.splice(found, 1)
        setVisibleLabels(prevVisibleLabels)
      }
    } catch (err) {
      console.error(err)
    }
  }

  const fetchInitialLabels = useCallback(async () => {
    const payload = {
      frequency: selectedFrequency,
      time_period: getPayloadTimePeriod(selectedTimePeriod)
    }

    setLoading(true)
    try {
      await updateLabels(payload)
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [updateLabels, selectedFrequency, selectedTimePeriod])

  const pinLabel = async (labelId: string) => {
    await axios.post(`/api/v3/labels/cpis/pin/${labelId}`)
  }

  const unpinLabel = async (labelId: string) => {
    await axios.delete('api/v3/labels/cpis/pin', { data: { ids: [labelId] } })
  }

  const handlePin = async (labelId: string) => {
    const labelIsPinned = labelsCount?.pinned_labels.find((n) => n.id === labelId)

    try {
      if (!labelIsPinned) {
        await pinLabel(labelId)
      } else {
        await unpinLabel(labelId)
      }

      await updateLabels()
    } catch (err) {
      console.error(err)
    }
  }

  const handleEdit = async (label: Label) => {
    await setEditLabel(label)
    await setLabelDrawerOpen(true)
    await setMode('edit')
    setEditLabel(label)
  }

  const handleChangeSeverity = async (event: SelectChangeEvent) => {
    if (!manifestData) return

    if (predictionModalOpen) {
      const value = event.target.value as CPISeverity
      const prunedValue: CPISeverity =
        value.includes('(') && value.includes(')') ? (value.slice(0, 3) as CPISeverity) : (value as CPISeverity)
      setSelectedSeverity(prunedValue)

      await refetchPredictionData({ thisCpiName: manifestData?.name || '', severity: prunedValue })
    } else {
      setRenderingChart(true)
      const value = event.target.value as CPISeverity
      const prunedValue: CPISeverity =
        value.includes('(') && value.includes(')') ? (value.slice(0, 3) as CPISeverity) : (value as CPISeverity)
      const { user_input: userInput } = manifestData.ui.trend_chart

      let sections: CPIManifestUIVisualizationParamSection[] = []
      if (userInput.visualization_param.sections.length > 1) {
        sections = userInput.visualization_param.sections
      }
      const thisSection = sections.find((n) => n.display_name === prunedValue)

      setSelectedSeverity(prunedValue)

      const integrationIds = getIntegrationIds(selectedDataSourceIds, isSnowflake, dataSourceOptions)

      const payload: CustomTrendsDataPayload = getPayloadTrendsChart({
        severity: thisSection ? (thisSection.name as CPISeverity) : prunedValue,
        selectedDataSourceIds: integrationIds,
        selectedTimePeriod,
        selectedFrequency,
        customTimePeriodValue,
        thisType: selectedType
      })
      const { stripes } = payload
      if (typeof stripes !== 'undefined') {
        delete payload.stripes
      }

      if (isSnowflake || !hasPredictionsEnabled) {
        const dataTrendsRes = await fetchTrendsData(payload, stripes ?? undefined)
        setChartData(dataTrendsRes ?? null)
      } else {
        const response = await refetchPredictionData({
          thisCpiName: manifestData.name || '',
          severity: thisSection ? (thisSection.name as CPISeverity) : prunedValue,
          timePeriod: payloadToEnumTimePeriod(payload.timePeriod || ''),
          selectedSources: selectedDataSourceIds
        })
        setChartData(response ?? null)
      }
    }
  }

  const renderSeverities = () => {
    if (!manifestData) return null
    const { user_input: userInput } = manifestData.ui.trend_chart
    const formulaParam = userInput.formula_params[0]
    const visualizationParamSection = userInput.visualization_param.sections

    if (visualizationParamSection.length <= 1) return null

    if (formulaParam?.sections.length > 0 || visualizationParamSection.length > 1) {
      let severities: string[] = []
      if (visualizationParamSection.length > 1) {
        severities = visualizationParamSection.map((n) => n.display_name as string)
      } else if (formulaParam?.sections.length > 0) {
        severities = formulaParam?.sections.map((n) => n.display_name as string)
      } else {
        return null
      }

      if (severities.length > 0 && severities.includes('All')) {
        severities = severities.map((c) => (c === 'All' ? `All (${severities.length - 1})` : c))
      }

      return (
        <TrendsChartSelectField
          options={severities}
          label=""
          placeholder="Filter by Severity"
          labeltext="Filter by: "
          value={selectedSeverity.includes('All') ? `All (${severities.length - 1})` : selectedSeverity}
          handleChange={handleChangeSeverity}
          disabled={chartData?.status !== DataStatus.Ready || renderingChart}
          minWidth="75px !important"
          hideSelected
          inlineLabel={`${getSingularSectionsName(userInput.visualization_param.sections_display_name)}:`}
        />
      )
    }

    return null
  }

  const activeChartValues = predictionModalOpen && predictionData ? predictionData.values : chartData?.values
  /* Initializing draws the same empty frame, and is the one state that can promise a first sync. */
  const isChartEmpty =
    initialLoad &&
    (chartData?.status === DataStatus.Ready || chartData?.status === DataStatus.Initializing) &&
    !activeChartValues?.length

  /* Sent only when true: no selected source has loaded a record, so the sync is still owed. */
  const awaitingFirstSync = !!chartData && instanceOfSnowflake(chartData) && !!chartData.awaitingFirstSync

  return (
    <>
      <DataTrendsContainer
        status={chartData?.status}
        severity={currentSeverityStatus}
        zone={chartData ? chartData.zoneColor : 'initializing'}
        className="data-trends-container"
        isPredictionMode={predictionModalOpen ? 'true' : 'false'}
      >
        <ControlsBar>
          <DataTrendsControls
            renderSeverities={renderSeverities}
            fetchTrendsData={fetchTrendsData}
            hasPredictionsEnabled={hasPredictionsEnabled}
            cpiName={cpi?.name || ''}
            fetchCustomLabelsCount={fetchCustomLabelsCount}
            updateLabels={updateLabels}
          />

              {hasPredictionsEnabled && (
                <OnyxAIWrapper has_model={predictionModelAvailable ? 'true' : 'false'}>
                  {predictionModelAvailable ? (
                    <>
                      {predictionModalOpen ? (
                        <ThemeButton
                          themevariant="primary"
                          className="onyxai-btn"
                          onClick={() => handlePredictionModalClose(false)}
                          sx={predictionModeExitBtnSx}
                        >
                          <>
                            <ExitPredictionModeIcon />
                            Exit Prediction Mode
                          </>
                        </ThemeButton>
                      ) : (
                        <ThemeButton
                          themevariant="primary"
                          className="onyxai-btn"
                          onClick={handlePredictionModalOpen}
                          sx={predictBtnSx}
                        >
                          <>
                            <OnyxAIPredictIcon />
                            Predict with OnyxAI
                          </>
                        </ThemeButton>
                      )}
                    </>
                  ) : (
                    <OnyxAITooltip
                      placement="bottom-end"
                      title={
                        <>
                          <OnyxAITooltipWrapper>
                            <Typography className="tooltip-title">Come back soon!</Typography>
                            <Typography className="tooltip-text">
                              Please come back in {daysUntilDataAvailable} days. OnyxAI needs to collect data for 2
                              months until it can predict a trend.
                            </Typography>
                          </OnyxAITooltipWrapper>
                        </>
                      }
                    >
                      <ThemeButton
                        themevariant="primary"
                        className="onyxai-btn disabled-btn"
                        sx={predictDisabledBtnSx}
                        disableTouchRipple
                      >
                        <>
                          <OnyxAIPredictIcon />
                          Predict with OnyxAI
                        </>
                      </ThemeButton>
                    </OnyxAITooltip>
                  )}
                </OnyxAIWrapper>
              )}
        </ControlsBar>

        <TrendsChartDescription
          ref={descriptionWrapperRef as any}
          status={chartData?.status}
          zone={chartData ? chartData.zoneColor : 'initializing'}
          chartData={chartData}
          selectedSeverity={selectedSeverity}
          setDrawerOpen={setLabelDrawerOpen}
          renderSeverities={renderSeverities}
          defaultLastDayProps={defaultLastDayProps}
          labelCount={labelCount}
          initialLoad={initialLoad}
        />
        {descriptionWrapperRef && (
          <Box className="chart-container">
            <div ref={containerRef} className="trends-d3-wrapper">
              <TrendsChart
                ref={{
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  containerRef,
                  svgRef
                }}
                visibleLabels={visibleLabels}
                setWidth={setWidth}
                width={width}
                height={CHART_HEIGHT}
                margin={margin}
                handlePin={handlePin}
                handleEdit={handleEdit}
                chartStatus={chartData?.status}
              />
              {isChartEmpty && (
                <NoTrendData
                  {...getNoTrendDataCopy(manifestData, chartData?.status, awaitingFirstSync, selectedSeverity)}
                />
              )}
              {slaChip && (
                <div
                  className="sla-chip-overlay"
                  style={{
                    position: 'absolute',
                    left: slaChip.x,
                    top: slaChip.y,
                    width: slaChip.w,
                    height: slaChip.h,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '0 16px',
                    borderRadius: 16,
                    background: 'var(--sla-chip-bg)',
                    WebkitBackdropFilter: 'blur(4px)',
                    backdropFilter: 'blur(4px)',
                    color: 'var(--text-primary)',
                    fontFamily: 'Quicksand',
                    fontSize: 14,
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: '0.1px',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none'
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                    <path
                      d="M7.5 3.75H6C4.75736 3.75 3.75 4.75736 3.75 6V7.5M16.5 3.75H18C19.2426 3.75 20.25 4.75736 20.25 6V7.5M20.25 16.5V18C20.25 19.2426 19.2426 20.25 18 20.25H16.5M7.5 20.25H6C4.75736 20.25 3.75 19.2426 3.75 18V16.5M15 12C15 13.6569 13.6569 15 12 15C10.3431 15 9 13.6569 9 12C9 10.3431 10.3431 9 12 9C13.6569 9 15 10.3431 15 12Z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <span>{slaChip.text}</span>
                </div>
              )}
            </div>

            <Box className="chart-footer">
              <Box className="legend-wrapper">
                <Box className="legend-item">
                  <TrendsPerformanceIcon />
                  <Typography>Performance</Typography>
                </Box>

                {predictionModalOpen && (
                  <Box className="legend-item">
                    <PredictedPerformanceIcon />
                    <Typography>Predicted Performance</Typography>
                  </Box>
                )}

                <Box className="legend-item">
                  <LegendSlaIcon />
                  <Typography>SLA</Typography>
                </Box>

                <Box className="legend-item">
                  <LegendLabelsIcon />
                  <Typography>Labels</Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}
      </DataTrendsContainer>

      <LabelsDrawer
        chartDateRange={chartDateRange}
        editedLabel={editLabel}
        loading={loading}
        appliedFilter={appliedFilter}
        setLoading={setLoading}
        customDate={customDate}
        cpiName={cpi?.name}
        cpiConfId={cpi?.configuration?.id}
        setDrawerOpen={setLabelDrawerOpen}
        updateLabels={updateLabels}
        updateLabelsAfterMutation={updateLabelsAfterMutation}
        filterLabelsData={filterLabelsData}
        selectedTimePeriod={selectedTimePeriod}
        selectedFrequency={selectedFrequency}
        customLabels={customLabels}
        customLabelsCount={customLabelsCount}
        unpinLabel={unpinLabel}
        pinLabel={pinLabel}
        visibleLabels={visibleLabels}
        setVisibleLabels={setVisibleLabels}
        deleteLabelCallback={deleteLabelCallback}
      />
    </>
  )
}

export default TrendsTab
